import React from 'react';
import {
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonOutlined as PersonIcon,
} from '@mui/icons-material';

import type { OnsiteStaffSectionProps } from '../../types/property.types';

import { commonButtonStyles } from '@/components/styles/common-style';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles/common-style';

export const OnsiteStaffSection: React.FC<OnsiteStaffSectionProps> = ({
  formData,
  onUpdateField,
  onAddFieldStaff,
  onUpdateFieldStaff,
  onRemoveFieldStaff,
  getFieldError,
  isFieldValid,
  originalFormData,
}) => {
  const maxStaffMembers = 10;
  const currentCount = formData.field_staffs?.length || 0;
  const canAddMore = currentCount < maxStaffMembers;

  const renderAddButton = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          onClick={onAddFieldStaff}
          disabled={!canAddMore}
          sx={{
            ...commonButtonStyles,
            px: 3,
          }}
        >
          入力行を追加 ({currentCount}/{maxStaffMembers})
        </Button>
      </Box>
    );
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '40px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
        }}
      >
        現場担当
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 生コン担当者 Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              sx={{
                color: '#1A1C20',
                fontSize: '18px',
                fontWeight: 600,
                mr: 1,
              }}
            >
              生コン担当者
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              label='生コン担当者 氏名'
              placeholder='氏名を入力'
              value={formData.ready_mix_concrete_staff_name || ''}
              onChange={(e) => onUpdateField('ready_mix_concrete_staff_name', e.target.value)}
              error={!isFieldValid('ready_mix_concrete_staff_name')}
              helperText={getFieldError('ready_mix_concrete_staff_name')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 50 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.ready_mix_concrete_staff_name,
                    formData.ready_mix_concrete_staff_name
                  ),
                },
              }}
            />
            <TextField
              fullWidth
              label='生コン担当者 TEL'
              placeholder='電話番号を入力'
              value={formData.ready_mix_concrete_staff_tel || ''}
              onChange={(e) => onUpdateField('ready_mix_concrete_staff_tel', e.target.value)}
              error={!isFieldValid('ready_mix_concrete_staff_tel')}
              helperText={getFieldError('ready_mix_concrete_staff_tel')}
              slotProps={{
                input: {
                  inputProps: { maxLength: 20 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.ready_mix_concrete_staff_tel,
                    formData.ready_mix_concrete_staff_tel
                  ),
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 現場担当者 Section */}
        <Box>
          <Typography
            variant='h6'
            sx={{
              color: '#1A1C20',
              fontSize: '18px',
              fontWeight: 600,
              mb: 2,
            }}
          >
            現場担当者
          </Typography>

          {/* Staff Members List */}
          {formData.field_staffs && formData.field_staffs.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {formData.field_staffs.map((staff, index) => {
                  const staffId = 'id' in staff ? staff.id : index;
                  const originalStaffData =
                    staff.index !== undefined
                      ? originalFormData?.field_staffs?.[staff.index]
                      : undefined;
                  return (
                    <Card key={String(staffId)} sx={{ border: '1px solid #E0E0E0' }}>
                      <CardHeader
                        avatar={<PersonIcon sx={{ color: '#1976d2' }} />}
                        title={`現場担当者 #${index + 1}`}
                        action={
                          <IconButton
                            onClick={() => onRemoveFieldStaff(staffId)}
                            sx={{ color: '#f44336' }}
                            size='small'
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                        sx={{ pb: 1 }}
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                          }}
                        >
                          <TextField
                            fullWidth
                            label='氏名'
                            placeholder='氏名を入力'
                            value={staff.name}
                            onChange={(e) => onUpdateFieldStaff(staffId, 'name', e.target.value)}
                            error={!isFieldValid(`field_staffs.${index}.name`)}
                            helperText={getFieldError(`field_staffs.${index}.name`)}
                            slotProps={{
                              input: {
                                inputProps: { maxLength: 20 },
                                sx: getStylesForEditableField(
                                  !!originalFormData,
                                  originalStaffData?.name,
                                  staff.name
                                ),
                              },
                            }}
                          />
                          <TextField
                            fullWidth
                            label='TEL'
                            placeholder='電話番号を入力'
                            value={staff.tel}
                            onChange={(e) => onUpdateFieldStaff(staffId, 'tel', e.target.value)}
                            error={!isFieldValid(`field_staffs.${index}.tel`)}
                            helperText={getFieldError(`field_staffs.${index}.tel`)}
                            slotProps={{
                              input: {
                                inputProps: { maxLength: 20 },
                                sx: getStylesForEditableField(
                                  !!originalFormData,
                                  originalStaffData?.tel,
                                  staff.tel
                                ),
                              },
                            }}
                          />
                          <TextField
                            fullWidth
                            label='MAIL'
                            placeholder='メールアドレスを入力'
                            value={staff.email}
                            onChange={(e) => onUpdateFieldStaff(staffId, 'email', e.target.value)}
                            error={!isFieldValid(`field_staffs.${index}.email`)}
                            helperText={getFieldError(`field_staffs.${index}.email`)}
                            slotProps={{
                              input: {
                                inputProps: { maxLength: 100 },
                                sx: getStylesForEditableField(
                                  !!originalFormData,
                                  originalStaffData?.email,
                                  staff.email
                                ),
                              },
                            }}
                            type='email'
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              <Box sx={{ mb: 3 }}>{renderAddButton()}</Box>
            </>
          ) : (
            /* Empty State */
            <Box
              sx={{
                border: '2px dashed #E0E0E0',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
              }}
            >
              <PersonIcon sx={{ fontSize: 48, color: '#BDBDBD', mb: 2 }} />
              <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                現場担当者
              </Typography>
              {/* Add Staff Button */}
              {renderAddButton()}
            </Box>
          )}
        </Box>
      </Box>
    </PaperBoxStyles>
  );
};
